"use client";

import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { SidebarWithCollapsibleSections } from "@package/subframe/components/SidebarWithCollapsibleSections";
import * as SubframeCore from "@subframe/core";
import {
  FeatherActivity,
  FeatherAlertTriangle,
  FeatherArchive,
  FeatherArrowRight,
  FeatherBarChart2,
  FeatherBox,
  FeatherBuilding,
  FeatherCalendar,
  FeatherCheckSquare,
  FeatherClipboardCheck,
  FeatherClock,
  FeatherDoorOpen,
  FeatherGlobe,
  FeatherHome,
  FeatherLayoutTemplate,
  FeatherMessageSquare,
  FeatherSettings,
  FeatherShield,
  FeatherShieldCheck,
  FeatherUsers,
} from "@subframe/core";
import { Link, useLocation } from "@tanstack/react-router";
import * as React from "react";

import { env } from "@/config/env";
import { useAuth, type UserRoleType } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { shouldShowNurserySwitcher } from "@/lib/roles";

/** Roles that get the full "manager dashboard" navigation. */
const MANAGER_ROLES: UserRoleType[] = [
  "NURSERY_MANAGER",
  "ORG_ADMIN",
  "PLATFORM_ADMIN",
];

function WorkspaceSwitcherHeader() {
  const { selectedNursery, nurseries, setSelectedNursery } = useNursery();
  const { organisations } = useOrganisation();

  const orgName = React.useMemo(() => {
    if (!selectedNursery) return "";
    const org = organisations.find(
      (o) => o.id === selectedNursery.organisationId,
    );
    return org?.name || "";
  }, [selectedNursery, organisations]);

  const handleNurserySelect = React.useCallback(
    (nurseryId: string, orgId: string) => {
      setSelectedNursery(nurseryId, orgId);
    },
    [setSelectedNursery],
  );

  const nurseryName = selectedNursery?.name || "Select Nursery";
  const nurseryLocation =
    selectedNursery?.address?.city || orgName || "No nursery selected";

  // Single-nursery users (and org managers whose group has one nursery) see the
  // nursery name as plain text; only multi-nursery users get the switcher.
  if (!shouldShowNurserySwitcher(nurseries.length)) {
    return (
      <div
        data-testid="nursery-name"
        className="flex w-full items-center gap-3 rounded-md px-2 py-2"
      >
        <img
          className="h-6 w-6 flex-none rounded object-cover"
          src="/images/logo.svg"
          alt="Organisation logo"
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start">
          <span className="line-clamp-1 text-body-bold font-body-bold text-default-font">
            {nurseryName}
          </span>
          <span className="line-clamp-1 text-caption font-caption text-subtext-color">
            {nurseryLocation}
          </span>
        </div>
      </div>
    );
  }

  return (
    <SubframeCore.DropdownMenu.Root>
      <SubframeCore.DropdownMenu.Trigger asChild>
        <SidebarWithCollapsibleSections.WorkspaceSwitcher
          data-testid="nursery-switcher"
          nurseryName={nurseryName}
          nurseryLocation={nurseryLocation}
          organisationLogo="/images/logo.svg"
        />
      </SubframeCore.DropdownMenu.Trigger>
      <SubframeCore.DropdownMenu.Portal>
        <SubframeCore.DropdownMenu.Content
          side="bottom"
          align="start"
          sideOffset={4}
          asChild
        >
          <DropdownMenu className="z-50">
            {nurseries.map((nursery) => {
              const nurseryOrg = organisations.find(
                (o) => o.id === nursery.organisationId,
              );
              return (
                <DropdownMenu.DropdownItem
                  key={nursery.id}
                  icon={null}
                  className={
                    selectedNursery?.id === nursery.id ? "bg-neutral-100" : ""
                  }
                  onSelect={() =>
                    handleNurserySelect(nursery.id, nursery.organisationId)
                  }
                >
                  {nursery.name}
                  {nurseryOrg && (
                    <span className="ml-1 text-caption text-subtext-color">
                      ({nurseryOrg.name})
                    </span>
                  )}
                </DropdownMenu.DropdownItem>
              );
            })}
          </DropdownMenu>
        </SubframeCore.DropdownMenu.Content>
      </SubframeCore.DropdownMenu.Portal>
    </SubframeCore.DropdownMenu.Root>
  );
}

function HelpCard() {
  const helpCentreUrl = env.VITE_HELP_CENTRE_URL;

  return (
    <div className="m-3 flex flex-col items-start gap-3 overflow-hidden rounded-2xl border border-solid border-secondary-beige-200 bg-gradient-to-br from-secondary-beige-50 to-primary-navy-50 p-4">
      <div className="flex w-full items-center gap-3">
        <img
          src="/images/help-avatar.png"
          alt=""
          className="h-12 w-12 flex-none rounded-full object-cover shadow-sm"
        />
        <div className="flex min-w-0 flex-col">
          <span className="text-body-bold font-body-bold text-primary-navy-800">
            Need a hand?
          </span>
          <span className="text-caption font-caption text-subtext-color">
            We’re here to help
          </span>
        </div>
      </div>
      <p className="text-caption font-caption text-subtext-color">
        Visit our help centre for friendly guides and support whenever you need
        it.
      </p>
      {helpCentreUrl ? (
        <a
          href={helpCentreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center gap-1 rounded-full bg-primary-navy-800 px-3 py-2 text-center text-caption-bold font-caption-bold text-balance text-white transition hover:bg-primary-navy-900"
        >
          Go to help centre
          <FeatherArrowRight className="shrink-0 text-caption" />
        </a>
      ) : (
        <button
          type="button"
          disabled
          className="inline-flex w-full cursor-not-allowed items-center justify-center gap-1 rounded-full bg-primary-navy-800 px-3 py-2 text-center text-caption-bold font-caption-bold text-balance text-white opacity-50"
        >
          Go to help centre
          <FeatherArrowRight className="shrink-0 text-caption" />
        </button>
      )}
    </div>
  );
}

/** Indents a section's child items and draws the vertical "children" line. */
function SectionChildren({ children }: { children: React.ReactNode }) {
  return (
    <div className="ml-4 flex w-full flex-col items-start border-l border-solid border-secondary-beige-200 pl-1">
      {children}
    </div>
  );
}

/** A nav item that links to a real route. */
function NavLinkItem({
  to,
  icon,
  label,
  active,
  onNavigate,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link to={to} className="w-full cursor-pointer" onClick={onNavigate}>
      <SidebarWithCollapsibleSections.NavItem selected={active} icon={icon}>
        {label}
      </SidebarWithCollapsibleSections.NavItem>
    </Link>
  );
}

/** A placeholder nav item for a destination that has no screen yet (NAU-247:
 *  Incidents, Complaints, Risk Assessments, History, Reports, Assets). */
function NavStubItem({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <SidebarWithCollapsibleSections.NavItem
      icon={icon}
      className="cursor-default opacity-60"
      rightSlot={
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-caption font-caption text-subtext-color">
          Soon
        </span>
      }
    >
      {label}
    </SidebarWithCollapsibleSections.NavItem>
  );
}

function SidebarSkeleton() {
  return (
    <nav className="flex h-full w-60 flex-col items-start border-r border-solid border-secondary-beige-200 bg-default-background">
      <div className="flex w-full flex-col items-start gap-2 px-3 py-4">
        <div className="flex w-full items-center gap-3 rounded-md px-2 py-2">
          <div className="h-8 w-8 animate-pulse rounded bg-neutral-200" />
          <div className="flex grow flex-col gap-1">
            <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
            <div className="h-3 w-16 animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
      </div>
      <div className="flex w-full grow flex-col items-start gap-4 px-4 py-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex w-full items-center gap-2 px-3 py-2">
            <div className="h-4 w-4 animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-full animate-pulse rounded bg-neutral-200" />
          </div>
        ))}
      </div>
    </nav>
  );
}

/** Codex-style navigation for manager / admin roles. */
function ManagerNav({
  isActive,
  isSectionActive,
  onNavigate,
  isPlatformAdmin,
  orgSlug,
  isOrgAdmin,
}: {
  isActive: (url: string) => boolean;
  isSectionActive: (url: string) => boolean;
  onNavigate?: () => void;
  isPlatformAdmin: boolean;
  orgSlug?: string;
  isOrgAdmin: boolean;
}) {
  return (
    <>
      {/* Dashboard and People. While the People Hub is switched off, staff
          are managed from Configure → Staff below instead. */}
      <div className="flex w-full flex-col items-start">
        <NavLinkItem
          to="/dashboard"
          icon={<FeatherHome />}
          label="Dashboard"
          active={isActive("/dashboard")}
          onNavigate={onNavigate}
        />
        {env.VITE_PEOPLE_HUB_ENABLED && (
          <NavLinkItem
            to="/people"
            icon={<FeatherUsers />}
            label="People"
            active={isSectionActive("/people")}
            onNavigate={onNavigate}
          />
        )}
      </div>

      {/* Operations */}
      <SidebarWithCollapsibleSections.NavSection
        label="Operations"
        icon={<FeatherClipboardCheck />}
      >
        <SectionChildren>
          <Link
            to="/checklists"
            className="w-full cursor-pointer"
            onClick={onNavigate}
          >
            <SidebarWithCollapsibleSections.NavItem icon={<FeatherCalendar />}>
              Workspace
            </SidebarWithCollapsibleSections.NavItem>
          </Link>
          <NavLinkItem
            to="/checklist-manager"
            icon={<FeatherClipboardCheck />}
            label="Checklist Manager"
            active={isActive("/checklist-manager")}
            onNavigate={onNavigate}
          />
          <NavLinkItem
            to="/monitoring"
            icon={<FeatherActivity />}
            label="Monitoring"
            active={isActive("/monitoring")}
            onNavigate={onNavigate}
          />
          <NavLinkItem
            to="/task-manager"
            icon={<FeatherCheckSquare />}
            label="Task Manager"
            active={isActive("/task-manager")}
            onNavigate={onNavigate}
          />
          <NavLinkItem
            to="/compliance-manager"
            icon={<FeatherShieldCheck />}
            label="Compliance"
            active={isActive("/compliance-manager")}
            onNavigate={onNavigate}
          />
          <NavLinkItem
            to="/event-records"
            icon={<FeatherAlertTriangle />}
            label="Events Hub"
            active={isActive("/event-records")}
            onNavigate={onNavigate}
          />
          <NavLinkItem
            to="/evidence-hub"
            icon={<FeatherArchive />}
            label="Evidence Hub"
            active={isActive("/evidence-hub")}
            onNavigate={onNavigate}
          />
          {/* The Events Hub above lists incidents and complaints too, so these two
              stubs are now partly redundant — left in place pending Debbie's call
              on whether they become dedicated pages (out of scope for NAU-341). */}
          <NavStubItem icon={<FeatherAlertTriangle />} label="Incidents" />
          <NavStubItem icon={<FeatherMessageSquare />} label="Complaints" />
          <NavStubItem icon={<FeatherShield />} label="Risk Assessments" />
          <NavStubItem icon={<FeatherClock />} label="History" />
        </SectionChildren>
      </SidebarWithCollapsibleSections.NavSection>

      {/* Checks & Audit Templates (single item) */}
      <div className="flex w-full flex-col items-start">
        <NavLinkItem
          to="/templates"
          icon={<FeatherLayoutTemplate />}
          label="Checks & Audit Templates"
          active={isActive("/templates")}
          onNavigate={onNavigate}
        />
        <NavStubItem icon={<FeatherBarChart2 />} label="Reports" />
      </div>

      {/* Configure */}
      <SidebarWithCollapsibleSections.NavSection
        label="Configure"
        icon={<FeatherSettings />}
      >
        <SectionChildren>
          {!env.VITE_PEOPLE_HUB_ENABLED && (
            <NavLinkItem
              to="/settings/staff"
              icon={<FeatherUsers />}
              label="Staff"
              active={isSectionActive("/settings/staff")}
              onNavigate={onNavigate}
            />
          )}
          <NavLinkItem
            to="/rooms"
            icon={<FeatherDoorOpen />}
            label="Rooms"
            active={isActive("/rooms")}
            onNavigate={onNavigate}
          />
          <NavStubItem icon={<FeatherBox />} label="Assets" />
        </SectionChildren>
      </SidebarWithCollapsibleSections.NavSection>

      {/* Admin extras */}
      {isPlatformAdmin && (
        <SidebarWithCollapsibleSections.NavSection
          label="Admin"
          icon={<FeatherBuilding />}
        >
          <SectionChildren>
            <NavLinkItem
              to="/organisations"
              icon={<FeatherBuilding />}
              label="Nursery Groups"
              active={isActive("/organisations")}
              onNavigate={onNavigate}
            />
            <NavLinkItem
              to="/global-templates"
              icon={<FeatherGlobe />}
              label="Global templates"
              active={isActive("/global-templates")}
              onNavigate={onNavigate}
            />
            <NavLinkItem
              to="/platform-settings"
              icon={<FeatherSettings />}
              label="Platform settings"
              active={isSectionActive("/platform-settings")}
              onNavigate={onNavigate}
            />
          </SectionChildren>
        </SidebarWithCollapsibleSections.NavSection>
      )}
      {/* Group admins only. `orgSlug` alone is NOT a proxy for that: NurseryContext
          sets the active organisation from whichever nursery is selected, so every
          nursery member has a slug — which used to give plain nursery managers this
          link, and with it the group-admin-only nursery configuration screens. */}
      {!isPlatformAdmin && isOrgAdmin && orgSlug && (
        <SidebarWithCollapsibleSections.NavSection
          label="Admin"
          icon={<FeatherBuilding />}
        >
          <SectionChildren>
            <Link
              to="/organisations/$slug"
              params={{ slug: orgSlug }}
              className="w-full cursor-pointer"
              onClick={onNavigate}
            >
              <SidebarWithCollapsibleSections.NavItem
                selected={isActive(`/organisations/${orgSlug}`)}
                icon={<FeatherBuilding />}
              >
                Nursery Group
              </SidebarWithCollapsibleSections.NavItem>
            </Link>
          </SectionChildren>
        </SidebarWithCollapsibleSections.NavSection>
      )}
    </>
  );
}

/** Simpler navigation for org members and nursery staff. */
function BasicNav({
  isActive,
  isSectionActive,
  showDashboard,
  showEventsHub,
  showPeople,
  onNavigate,
}: {
  isActive: (url: string) => boolean;
  isSectionActive: (url: string) => boolean;
  showDashboard: boolean;
  /** Deputy managers acknowledge events, so they need the Hub (NAU-342). */
  showEventsHub: boolean;
  /** Deputies view colleagues' profiles, so they need People (NAU-431). */
  showPeople: boolean;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex w-full flex-col items-start">
      {showDashboard && (
        <NavLinkItem
          to="/dashboard"
          icon={<FeatherHome />}
          label="Dashboard"
          active={isActive("/dashboard")}
          onNavigate={onNavigate}
        />
      )}
      <NavLinkItem
        to="/checklists"
        icon={<FeatherClipboardCheck />}
        label="Workspace"
        active={isActive("/checklists")}
        onNavigate={onNavigate}
      />
      {/* Task Manager is open to all roles; the API narrows the rows to the
          work each user may see. */}
      <NavLinkItem
        to="/task-manager"
        icon={<FeatherCheckSquare />}
        label="Task Manager"
        active={isActive("/task-manager")}
        onNavigate={onNavigate}
      />
      {/* Deputy managers can acknowledge events but were shown BasicNav, which
          had no way to the Events Hub — so their only route to a record was a
          direct link (NAU-341 flagged this; NAU-342 fixes it). Adding single
          links (this and People) rather than promoting them to ManagerNav,
          which would also hand them Checklist Manager, Compliance, Templates
          and Rooms. */}
      {showEventsHub && (
        <NavLinkItem
          to="/event-records"
          icon={<FeatherAlertTriangle />}
          label="Events Hub"
          active={isActive("/event-records")}
          onNavigate={onNavigate}
        />
      )}
      {showPeople && (
        <NavLinkItem
          to="/people"
          icon={<FeatherUsers />}
          label="People"
          active={isSectionActive("/people")}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

interface AppSidebarProps {
  className?: string;
  isMobileOpen?: boolean;
  isDesktopOpen?: boolean;
  onCloseMobile?: () => void;
}

export function AppSidebar({
  className,
  isMobileOpen = false,
  isDesktopOpen = true,
  onCloseMobile,
}: AppSidebarProps) {
  const { userRoleType, isLoading } = useAuth();
  const { activeOrg, isOrgAdmin } = useOrganisation();
  const location = useLocation();
  const pathname = location.pathname;

  const isManager = MANAGER_ROLES.includes(userRoleType);
  const isOrgMember = userRoleType === "ORG_MEMBER";
  // Deputies acknowledge events and view colleagues' profiles, so they get the
  // Events Hub (NAU-342) and People (NAU-431) links.
  const isDeputyManager = userRoleType === "NURSERY_DEPUTY_MANAGER";

  const isActive = React.useCallback(
    (url: string) => pathname === url,
    [pathname],
  );

  // Matches a nav section and any of its subroutes (e.g. /platform-settings
  // stays active on /platform-settings/categories).
  const isSectionActive = React.useCallback(
    (url: string) => pathname === url || pathname.startsWith(`${url}/`),
    [pathname],
  );

  const showSkeleton = isLoading || userRoleType === "UNKNOWN";
  if (showSkeleton) {
    return <SidebarSkeleton />;
  }

  const desktopNav = isManager ? (
    <ManagerNav
      isActive={isActive}
      isSectionActive={isSectionActive}
      isPlatformAdmin={userRoleType === "PLATFORM_ADMIN"}
      orgSlug={activeOrg?.slug}
      isOrgAdmin={isOrgAdmin}
    />
  ) : (
    <BasicNav
      isActive={isActive}
      isSectionActive={isSectionActive}
      showDashboard={isOrgMember}
      showEventsHub={isDeputyManager}
      showPeople={isDeputyManager && env.VITE_PEOPLE_HUB_ENABLED}
    />
  );

  // Desktop sidebar (toggleable with animation)
  const desktopSidebar = (
    <div
      className={`relative z-40 mobile:hidden h-full shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
        isDesktopOpen ? "w-60" : "w-0"
      }`}
    >
      <SidebarWithCollapsibleSections
        className={`h-full transition-transform duration-300 ease-in-out ${
          isDesktopOpen ? "translate-x-0" : "-translate-x-full"
        } ${className || ""}`}
        header={<WorkspaceSwitcherHeader />}
        footer={<HelpCard />}
      >
        {desktopNav}
      </SidebarWithCollapsibleSections>
    </div>
  );

  // Mobile sidebar (overlay) — pared down to the Workspace, the only mobile-ready
  // screen.
  const mobileSidebar = (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/50 hidden mobile:block transition-opacity duration-300 ease-in-out ${
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onCloseMobile}
      />
      <div
        className={`fixed inset-y-0 left-0 z-50 hidden mobile:block transition-transform duration-300 ease-in-out ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarWithCollapsibleSections
          className={className}
          header={<WorkspaceSwitcherHeader />}
        >
          <div className="flex w-full flex-col items-start">
            <NavLinkItem
              to="/checklists"
              icon={<FeatherClipboardCheck />}
              label="Workspace"
              active={isActive("/checklists")}
              onNavigate={onCloseMobile}
            />
          </div>
        </SidebarWithCollapsibleSections>
      </div>
    </>
  );

  return (
    <>
      {desktopSidebar}
      {mobileSidebar}
    </>
  );
}
